// frontend/src/components/charts/GraficoBarras.jsx (versão 0.2.0)

import React, { useState, useEffect } from 'react';
// Importações otimizadas - apenas o que é necessário
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CardContent from '../UI/CardContent';

const BAR_COLOR = '#4f46e5'; // Cor indigo 600, combinando com o tema

/**
 * Componente de Gráfico de Barras para exibir a carga de trabalho.
 * @param {Array<Object>} data - Dados para o gráfico. Ex: [{ profissional_nome: 'Daniel', total_tarefas: 6 }]
 * @param {string} dataKey - Chave do valor no objeto de dados (ex: 'total_tarefas')
 * @param {string} nameKey - Chave do nome/rótulo no eixo X (ex: 'profissional_nome')
 * @param {string} title - Título do gráfico
 * @param {string} subtitle - Subtítulo/descrição
 */
const GraficoBarras = ({ data, dataKey = 'total_tarefas', nameKey = 'profissional_nome', title, subtitle }) => {
    // Hook para detectar tamanho da tela
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    // Mapeamento simples (não é estritamente necessário, mas é uma boa prática
    // para garantir que as chaves X e Y sejam facilmente configuráveis)
    const chartData = data && Array.isArray(data) 
        ? data.map(item => ({
            name: item[nameKey],
            value: item[dataKey]
        }))
        : [];

    if (!chartData || chartData.length === 0) {
        return (
            <CardContent>
            <h3 className="text-base sm:text-lg font-semibold mb-2">{title}</h3>
            <p className="text-gray-500 mb-3 sm:mb-4 text-xs sm:text-sm">{subtitle}</p>
            <div className="h-48 sm:h-56 md:h-64 flex items-center justify-center">
                    <p className="text-gray-500">Sem dados para exibir.</p>
                </div>
            </CardContent>
        );
    }

    // Configuração do Tooltip (hover)
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="p-2 bg-white border border-gray-300 rounded shadow-lg text-sm">
                    <p className="font-bold text-gray-700">{label}</p>
                    <p className="text-blue-600">{`${payload[0].value} tarefas`}</p>
                </div>
            );
        }
        return null;
    };


    return (
        <CardContent>
            <h3 className="text-base sm:text-lg font-semibold mb-2">{title}</h3>
            <p className="text-gray-500 mb-3 sm:mb-4 text-xs sm:text-sm">{subtitle}</p>
            <div className="h-48 sm:h-56 md:h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{ 
                            top: 10, 
                            right: 10, 
                            left: isMobile ? -10 : -20, // Ajuste para mobile
                            bottom: isMobile ? 40 : 5 
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="name" 
                            interval={0} // Garante que todos os nomes apareçam
                            angle={isMobile ? -45 : -30} // Rotação maior em mobile
                            textAnchor="end"
                            height={isMobile ? 60 : 50}
                            stroke="#555"
                            style={{ fontSize: isMobile ? '9px' : '10px' }}
                        />
                        <YAxis 
                            allowDecimals={false} 
                            stroke="#555"
                            width={isMobile ? 40 : 60}
                            style={{ fontSize: isMobile ? '9px' : '10px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            verticalAlign="top" 
                            height={36}
                            wrapperStyle={{ fontSize: '12px' }}
                            iconSize={10}
                            payload={[{ 
                                value: 'Total de Tarefas', 
                                type: 'square', 
                                color: BAR_COLOR 
                            }]}
                        />
                        <Bar 
                            dataKey="value" 
                            fill={BAR_COLOR} 
                            name="Total de Tarefas" 
                            radius={[4, 4, 0, 0]} // Barras arredondadas no topo
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </CardContent>
    );
};

export default GraficoBarras;